import { useEffect } from "react";
import { remiLogger } from "@remi/logging";
import { defaultMap, isOneOf } from "@remi/ts-utils";
import {
  type CommandKey,
  CommandKeys,
  FirstCommandKeys,
  type Key,
  type KeyboardShortcut,
  keyToVal,
  NonCommandKeys,
  SecondCommandKeys,
} from "@remi/types/keyboard";

import { getOs } from "../utils/os";

const getSortedKeys = (keys: string[]) => {
  const filteredKeys = keys.filter((key) => isOneOf(key, [...NonCommandKeys, ...CommandKeys]));
  // put command keys at beginning, second command keys next, and other keys last, sorted internally within each group by alphabetical order
  const sortedKeys = filteredKeys.sort((a, b) => {
    if (isOneOf(a, FirstCommandKeys)) {
      if (isOneOf(b, FirstCommandKeys)) {
        return a.localeCompare(b);
      }
      return -1;
    }
    if (isOneOf(b, FirstCommandKeys)) {
      return 1;
    }
    if (isOneOf(a, SecondCommandKeys)) {
      if (isOneOf(b, SecondCommandKeys)) {
        return a.localeCompare(b);
      }
      return -1;
    }
    if (isOneOf(b, SecondCommandKeys)) {
      return 1;
    }
    return a.localeCompare(b);
  });
  return sortedKeys;
};

export const getShortcutString = (keys: string[]) => {
  const displayMap: Partial<Record<Key, string>> = {
    enter: "↵",
    arrowdown: "↓",
    arrowup: "↑",
    arrowleft: "←",
    arrowright: "→",
    control: getOs() === "mac" ? "⌘" : "ctrl",
    alt: getOs() === "mac" ? "⌥" : "alt",
    shift: "⇧",
    escape: "esc",
    backspace: "⌫",
    space: "␣",
  };
  const sortedKeys = getSortedKeys(keys);
  return sortedKeys.map((key) => displayMap[key] ?? key).join(" + ");
};

const getShortcutId = (shortcut: KeyboardShortcut) => {
  const { keys, name } = shortcut;
  const shortcutKey = getShortcutString(keys);
  return `${shortcutKey}-${name}`;
};

const registeredShortcuts = defaultMap<string, (KeyboardShortcut & { id: string })[]>(() => []);

const registerShortcut = (shortcut: KeyboardShortcut) => {
  const { keys, handler, disabled, name } = shortcut;
  const shortcutKey = getShortcutString(keys);
  const id = getShortcutId(shortcut);
  const shortcuts = registeredShortcuts[shortcutKey];
  if (shortcuts) {
    if (shortcuts.length > 0) {
      const existingShortcut = shortcuts.find((s) => s.name === name);
      if (existingShortcut) {
        existingShortcut.handler = handler;
        existingShortcut.disabled = disabled;
      } else {
        shortcuts.push({ ...shortcut, id });
      }
    } else {
      shortcuts.push({ ...shortcut, id });
    }
  } else {
    remiLogger.error("No shortcuts found for key.", { attributes: { shortcutKey } });
  }
};

const unregisterShortcut = (shortcut: KeyboardShortcut) => {
  const shortcutKey = getShortcutString(shortcut.keys);
  const id = getShortcutId(shortcut);
  const shortcuts = registeredShortcuts[shortcutKey];
  if (shortcuts) {
    const index = shortcuts.findIndex((s) => s.id === id);
    if (index !== -1) {
      shortcuts.splice(index, 1);
    }
  }
};

const getKey = (event: KeyboardEvent) => {
  if (!event.key) {
    return "";
  }
  const key = event.key.toLowerCase();
  return keyToVal[key] ?? key;
};

const getKeysPressed = (event: KeyboardEvent) => {
  const key = getKey(event);
  if (!isOneOf(key, NonCommandKeys)) {
    return [];
  }
  const commandsPressed: CommandKey[] = [];
  if (event.metaKey || event.ctrlKey) {
    commandsPressed.push("control");
  }
  if (event.shiftKey) {
    commandsPressed.push("shift");
  }
  if (event.altKey) {
    commandsPressed.push("alt");
  }
  return [...commandsPressed, key];
};

const handleKeydown = (event: KeyboardEvent) => {
  const keysPressed = getKeysPressed(event);
  if (keysPressed.length === 0) {
    return;
  }
  const shortcutKey = getShortcutString(keysPressed);
  const shortcuts = registeredShortcuts[shortcutKey];
  if (!shortcuts || shortcuts.length === 0) {
    return;
  }
  const shortcut = shortcuts[shortcuts.length - 1];
  if (!shortcut) {
    return;
  }
  event.preventDefault();
  if (shortcut.disabled !== true) {
    shortcut.handler();
  }
};

let listenerAdded = false;

export const useKeyboardShortcuts = (shortcuts: KeyboardShortcut[]) => {
  useEffect(() => {
    if (!listenerAdded) {
      if (typeof document === "undefined") {
        return;
      }
      document.addEventListener("keydown", handleKeydown);
      listenerAdded = true;
    }

    shortcuts.forEach(registerShortcut);
    return () => {
      shortcuts.forEach(unregisterShortcut);
    };
  }, [shortcuts]);
};

export const useKeyboardShortcut = (shortcut: KeyboardShortcut) => {
  return useKeyboardShortcuts([shortcut]);
};
